"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { Search, Headphones, Home as HomeIcon } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import type { Category } from "@/lib/types"

interface HomePageLayoutProps {
  children: React.ReactNode
  categories?: Category[]
}

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://dev.oxygentamil.com/api"

export function HomePageLayout({ children, categories: providedCategories = [] }: HomePageLayoutProps) {
  const [settings, setSettings] = useState({
    header_logo: null as string | null,
    footer_logo: null as string | null,
    header_bg_color: "#FFFFFF",
    footer_bg_color: "#FFFFFF",
    viral_news_box_bg_color: "#FFFFFF",
    categories_box_bg_color: "#FFFFFF",
    tags_box_bg_color: "#FFFFFF",
    font_size: "16px",
    font_family: "Poppins, sans-serif",
    copyright_text: "© {year} News Portal. All rights reserved.",
  })
  const [categories, setCategories] = useState<Category[]>(providedCategories)

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await fetch(`${API_BASE}/website-settings`)
        if (response.ok) {
          const data = await response.json()
          setSettings({
            header_logo: data.header_logo || null,
            footer_logo: data.footer_logo || null,
            header_bg_color: data.header_bg_color || "#FFFFFF",
            footer_bg_color: data.footer_bg_color || "#FFFFFF",
            viral_news_box_bg_color: data.viral_news_box_bg_color || "#FFFFFF",
            categories_box_bg_color: data.categories_box_bg_color || "#FFFFFF",
            tags_box_bg_color: data.tags_box_bg_color || "#FFFFFF",
            font_size: data.font_size || "16px",
            font_family: data.font_family || "Poppins, sans-serif",
            copyright_text: data.copyright_text || "© {year} News Portal. All rights reserved.",
          })
        }
      } catch (error) {
        console.error("Failed to load website settings:", error)
      }
    }
    loadSettings()
  }, [])

  // Fetch categories if not provided
  useEffect(() => {
    if (providedCategories.length === 0) {
      const fetchCategories = async () => {
        try {
          const response = await fetch(`${API_BASE}/public/categories`)
          if (response.ok) {
            const data = await response.json()
            setCategories(data.map((c: any) => ({ ...c, id: String(c.id) })))
          }
        } catch (error) {
          console.error("Failed to load categories:", error)
        }
      }
      fetchCategories()
    } else {
      setCategories(providedCategories)
    }
  }, [providedCategories])

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f3f3f5" }}>
      <style jsx global>{`
        :root {
          --header-bg-color: ${settings.header_bg_color};
          --footer-bg-color: ${settings.footer_bg_color};
          --viral-news-box-bg-color: ${settings.viral_news_box_bg_color};
          --categories-box-bg-color: ${settings.categories_box_bg_color};
          --tags-box-bg-color: ${settings.tags_box_bg_color};
        }
        body {
          font-size: ${settings.font_size};
          font-family: 'Signika Negative', 'Mukta Malar', sans-serif;
        }
        p {
          font-size: 16px;
          font-weight: 500;
          line-height: 24px;
          color: #555;
        }
      `}</style>
      {/* Header */}
      <header 
        className="sticky top-0 z-50 w-full border-b backdrop-blur supports-[backdrop-filter]:bg-opacity-95"
        style={{ backgroundColor: settings.header_bg_color }}
      >
        <div className="container mx-auto max-w-6xl">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link href="/" className="flex items-center space-x-2">
              {settings.header_logo ? (
                <img
                  src={settings.header_logo}
                  alt="Logo"
                  className="h-16 w-auto"
                />
              ) : (
                <span className="text-3xl font-bold">
                  News Portal
                </span>
              )}
            </Link>

            {/* Search */}
            <div className="flex items-center space-x-4">
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search articles..."
                  className="pl-10 w-64"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Sub Header - Home + Categories */}
      {(categories.length > 0) && (
        <div 
          className="w-full border-b"
          style={{ backgroundColor: "#D00501" }}
        >
          <div className="container mx-auto max-w-6xl">
            <nav className="flex items-center gap-4 py-3 overflow-x-auto">
              <Link
                href="/"
                className="flex items-center gap-1 text-sm font-medium text-white hover:text-gray-200 transition-colors whitespace-nowrap px-3 py-1 rounded-md hover:bg-opacity-80"
                style={{ color: "#FFFFFF" }}
                aria-label="Home"
                title="Home"
              >
                <HomeIcon className="h-4 w-4" />
              </Link>
              {categories.map((category) => (
                <Link
                  key={category.id}
                  href={`/category/${encodeURIComponent(String(category.slug ?? "").trim())}`}
                  className="text-sm font-medium text-white hover:text-gray-200 transition-colors whitespace-nowrap px-3 py-1 rounded-md hover:bg-opacity-80"
                  style={{ color: "#FFFFFF" }}
                >
                  {category.name}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto max-w-6xl">{children}</main>

      {/* Footer */}
      <footer 
        className="border-t mt-12"
        style={{ backgroundColor: settings.footer_bg_color }}
      >
        {/* Footer Help Desk Banner */}
        <div 
          className="w-full py-3"
          style={{ backgroundColor: "#4a4a4a" }}
        >
          <div className="container mx-auto max-w-6xl px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Left: Help Desk Section */}
              <div className="flex items-center gap-3">
                <Headphones className="h-8 w-8 text-blue-500" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-red-600 font-semibold text-sm md:text-base">Help Desk</span>
                    <a 
                      href="tel:8925992059" 
                      className="text-white text-sm md:text-base font-medium hover:underline"
                    >
                      (+91) 8925992059
                    </a>
                  </div>
                  <div className="text-gray-400 text-xs md:text-sm mt-0.5">
                    24/7 Customer Service
                  </div>
                </div>
              </div>

              {/* Right: Navigation Links */}
              <nav className="flex flex-wrap items-center justify-center md:justify-end gap-3 md:gap-4">
                <Link href="/" className="text-white text-xs md:text-sm font-medium uppercase hover:text-gray-300 transition-colors">
                  Home
                </Link>
                <Link href="/about-us" className="text-white text-xs md:text-sm font-medium uppercase hover:text-gray-300 transition-colors">
                  About
                </Link>
                <Link href="/contact-us" className="text-white text-xs md:text-sm font-medium uppercase hover:text-gray-300 transition-colors">
                  Contact Us
                </Link>
                <Link href="/user-policy" className="text-white text-xs md:text-sm font-medium uppercase hover:text-gray-300 transition-colors">
                  User Policy
                </Link>
                <Link href="/cookie-policy" className="text-white text-xs md:text-sm font-medium uppercase hover:text-gray-300 transition-colors">
                  Cookie Policy
                </Link>
                <Link href="/privacy-policy" className="text-white text-xs md:text-sm font-medium uppercase hover:text-gray-300 transition-colors">
                  Privacy Policy
                </Link>
              </nav>
            </div>
          </div>
        </div>

        <div className="container mx-auto max-w-6xl py-8">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              {settings.footer_logo ? (
                <img
                  src={settings.footer_logo}
                  alt="Logo"
                  className="h-10 w-auto mb-4"
                />
              ) : (
                <h3 className="font-semibold mb-4">News Portal</h3>
              )}
              <p className="text-sm text-muted-foreground">
              Oxygentamil brings you the latest updates on Tamil cinema, breaking entertainment news, exclusive videos, movie reviews, celebrity interviews, photos, teasers, trailers, and all trending stories from the world of Kollywood 24x7
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Categories</h3>
              {categories.length > 0 ? (
                <div className="flex flex-wrap items-center gap-4 py-1">
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      href={`/category/${encodeURIComponent(String(category.slug ?? "").trim())}`}
                      className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No categories available</p>
              )}
            </div>
          </div>
          <div className="mt-8 pt-8 border-t text-center md:text-right text-sm text-muted-foreground">
            {settings.copyright_text.replace("{year}", new Date().getFullYear().toString())}
          </div>
        </div>
      </footer>
    </div>
  )
}

